import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEncryption } from '../context/EncryptionContext';
import { ArrowLeft, User, Shield, Bell, Palette, Info, LogOut, Users, Lock } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { encryptionEnabled } = useEncryption();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  return (
    <div className="min-h-screen pb-16 bg-background-DEFAULT flex flex-col" data-testid="settings-page">
      <div className="p-6 border-b border-border-DEFAULT">
        <div className="chat-container flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/chats')}
            className="h-10 w-10 rounded-full flex items-center justify-center hover:bg-secondary-DEFAULT transition-colors"
          >
            <ArrowLeft size={20} className="text-text-primary" />
          </button>
          <h1 className="font-heading text-2xl text-text-primary">Settings</h1>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
        <div className="chat-container max-w-2xl mx-auto">
          {/* Profile Section */}
          <div className="mb-8">
            <div className="bg-white rounded-3xl p-6 shadow-card border border-border-DEFAULT">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C5A059] flex items-center justify-center text-white font-medium text-2xl">
                  {user?.display_name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-xl text-text-primary mb-1">{user?.display_name}</h2>
                  <p className="text-sm text-text-secondary mb-2">@{user?.username}</p>
                  <div className="inline-flex items-center gap-2 bg-secondary-DEFAULT px-3 py-1 rounded-full">
                    <span className="text-xs font-medium text-text-secondary">Invite Code:</span>
                    <span className="text-sm font-mono font-bold text-primary">{user?.invite_code}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Options */}
          <div className="space-y-3">
            <h2 className="text-sm font-medium text-text-secondary mb-3 px-2">ACCOUNT</h2>
            
            {user?.is_admin && (
              <button
                data-testid="admin-panel-btn"
                onClick={() => navigate('/admin')}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#C5A059] rounded-2xl p-6 shadow-gold hover:shadow-gold-glow transition-all flex items-center gap-4 mb-4"
              >
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Shield size={24} className="text-white" />
                </div>
                <div className="flex-1 text-left">
                  <h3 className="font-medium text-white">Admin Panel</h3>
                  <p className="text-sm text-white/80">Manage invite codes & stats</p>
                </div>
              </button>
            )}
            
            <button
              data-testid="profile-btn"
              onClick={() => navigate('/settings/profile')}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <User size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">Edit Profile</h3>
                <p className="text-sm text-text-secondary">Update your information</p>
              </div>
            </button>

            <button
              data-testid="privacy-btn"
              onClick={() => navigate('/settings/privacy')}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <Shield size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">Privacy & Security</h3>
                <p className="text-sm text-text-secondary">Read receipts, last seen, online status</p>
              </div>
            </button>

            <button
              data-testid="encryption-btn"
              onClick={() => navigate('/settings/encryption')}
              className={`w-full rounded-2xl p-6 shadow-sm border transition-all flex items-center gap-4 ${
                encryptionEnabled 
                  ? 'bg-green-50 border-green-200 hover:border-green-400' 
                  : 'bg-white border-border-DEFAULT hover:border-primary hover:shadow-md'
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                encryptionEnabled ? 'bg-green-100' : 'bg-secondary-DEFAULT'
              }`}>
                <Lock size={24} className={encryptionEnabled ? 'text-green-600' : 'text-primary'} />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">End-to-End Encryption</h3>
                <p className="text-sm text-text-secondary">
                  {encryptionEnabled ? 'Encryption is active' : 'Encrypt your messages'}
                </p>
              </div>
              {encryptionEnabled && (
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">ON</span>
              )}
            </button>

            <h2 className="text-sm font-medium text-text-secondary mb-3 px-2 mt-6">PREFERENCES</h2>

            <button
              data-testid="accounts-btn"
              onClick={() => navigate('/settings/accounts')}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <Users size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">Multiple Accounts</h3>
                <p className="text-sm text-text-secondary">Switch between accounts</p>
              </div>
            </button>

            <button
              data-testid="notifications-btn"
              onClick={() => navigate('/settings/notifications')}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <Bell size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">Notifications</h3>
                <p className="text-sm text-text-secondary">Configure notification settings</p>
              </div>
            </button>

            <button
              data-testid="appearance-btn"
              onClick={() => toast.info('Appearance settings coming soon')}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <Palette size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">Appearance</h3>
                <p className="text-sm text-text-secondary">Customize app theme</p>
              </div>
            </button>

            <h2 className="text-sm font-medium text-text-secondary mb-3 px-2 mt-6">MORE</h2>

            <button
              data-testid="about-btn"
              onClick={() => toast.info('ChatBuzzz v1.0.0 - Built with love')}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <Info size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">About</h3>
                <p className="text-sm text-text-secondary">App info and version</p>
              </div>
            </button>

            <button
              data-testid="notif-debug-btn"
              onClick={() => {
                const log = localStorage.getItem('notif_debug_log') || 'No notification taps recorded yet';
                const lastChatId = localStorage.getItem('notif_debug_chatId') || 'none';
                alert(`Last chatId: ${lastChatId}\n\nFull log:\n${log}`);
              }}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-border-DEFAULT hover:border-primary hover:shadow-md transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-secondary-DEFAULT flex items-center justify-center">
                <Info size={24} className="text-primary" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-text-primary">Notification Debug Log</h3>
                <p className="text-sm text-text-secondary">View notification click data</p>
              </div>
            </button>

            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="w-full bg-white rounded-2xl p-6 shadow-sm border border-status-error hover:bg-red-50 transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <LogOut size={24} className="text-status-error" />
              </div>
              <div className="flex-1 text-left">
                <h3 className="font-medium text-status-error">Log Out</h3>
                <p className="text-sm text-text-secondary">Sign out from this device</p>
              </div>
            </button>
          </div>
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default Settings;